import { useId } from 'react';
import { Label, RadioGroup, RadioGroupItem } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { briefTemplateLevelLabel } from '@/entities/brief-template';
import type { BriefTemplateCreateLevel } from './useBriefTemplateIntakeShell';

const WORKSPACE: BriefTemplateCreateLevel = 'workspace';
const ORGANIZATION: BriefTemplateCreateLevel = 'organization';

interface BriefTemplateLevelChoiceProps {
  value: BriefTemplateCreateLevel;
  onChange: (value: BriefTemplateCreateLevel) => void;
  disabled?: boolean;
}

/** The option is named by its level alone; where it is available is its description. */
function LevelOption({
  level,
  description,
}: {
  level: BriefTemplateCreateLevel;
  description: string;
}) {
  const itemId = useId();
  const descriptionId = useId();

  return (
    <div className="flex items-start gap-xs">
      <RadioGroupItem value={level} id={itemId} aria-describedby={descriptionId} />
      <div className="flex flex-col gap-3xs">
        <Label htmlFor={itemId}>{briefTemplateLevelLabel(level)}</Label>
        <span id={descriptionId} className="text-sm text-muted-foreground">
          {description}
        </span>
      </div>
    </div>
  );
}

/** Where a new template will be available. Rendered only for organization template managers. */
export function BriefTemplateLevelChoice({
  value,
  onChange,
  disabled = false,
}: BriefTemplateLevelChoiceProps) {
  const { t } = useTranslation('knowledge');
  const headingId = useId();

  return (
    <div className="flex flex-col gap-xs">
      <span id={headingId} className="text-sm font-medium text-foreground">
        {t('knowledgeHub.briefTemplateLevelChoice.availableIn')}
      </span>
      <RadioGroup
        aria-labelledby={headingId}
        value={value}
        disabled={disabled}
        onValueChange={(next: string) => onChange(next as BriefTemplateCreateLevel)}
        className="flex flex-col gap-xs"
      >
        <LevelOption
          level={WORKSPACE}
          description={t('knowledgeHub.briefTemplateLevelChoice.workspaceDescription')}
        />
        <LevelOption
          level={ORGANIZATION}
          description={t('knowledgeHub.briefTemplateLevelChoice.organizationDescription')}
        />
      </RadioGroup>
    </div>
  );
}
